import React from 'react';
import useLocalization from './useLocalization';
import { ConversationVariable, SystemVariable } from 'types/system/system-vars';

const useSystemVars = () => {
  const { currentLocale } = useLocalization();

  const { systemVars, systemVarsWithChildren, conversationVars } = React.useMemo(() => {
    const systemVars: Array<SystemVariable> = [
      {
        //* מזהה השיחה הנוכחית — מפתח יציב לשיחה למערכות חיצוניות
        varName:
          currentLocale.implementedVariables['$conversation.id']?.name || '$conversation.id',
        label:
          currentLocale.implementedVariables['$conversation.id']?.label || 'מזהה השיחה הנוכחית',
        valueProperty: 'SessionId',
        valueGetter: (chatUser) => chatUser?.SessionId || '',
      },
      {
        varName: currentLocale.implementedVariables['$contact.accountid'].name,
        label: currentLocale.implementedVariables['$contact.accountid'].label,
        valueProperty: 'Id',
        valueGetter: (chatUser) => chatUser?.Id || '',
      },
      {
        varName: currentLocale.implementedVariables['$contact.last_message'].name,
        label: currentLocale.implementedVariables['$contact.last_message'].label,
        valueProperty: 'LastMessage',
        valueGetter: (chatUser) => chatUser?.LastMessage || '',
      },
      {
        varName: currentLocale.implementedVariables['$contact.last_incoming_message'].name,
        label: currentLocale.implementedVariables['$contact.last_incoming_message'].label,
        valueProperty: 'IncomingLastMessage',
        valueGetter: (chatUser) => chatUser?.IncomingLastMessage || '',
      },
      {
        //* סוג ההודעה הנכנסת האחרונה — לניתוב כשאין טקסט (קולית/תמונה/קובץ)
        varName:
          currentLocale.implementedVariables['$contact.last_incoming_message_type']?.name ||
          '$contact.last_incoming_message_type',
        label:
          currentLocale.implementedVariables['$contact.last_incoming_message_type']?.label ||
          'סוג ההודעה הנכנסת האחרונה',
        valueProperty: 'IncomingLastMessageMediaType',
        valueGetter: (chatUser) => chatUser?.IncomingLastMessageMediaType || '',
      },
      {
        varName: currentLocale.implementedVariables['$contact.accountname'].name,
        label: currentLocale.implementedVariables['$contact.accountname'].label,
        valueProperty: 'FullName',
        valueGetter: (chatUser) => chatUser?.FullName || '',
      },
      {
        varName: currentLocale.implementedVariables['$contact.phone'].name,
        label: currentLocale.implementedVariables['$contact.phone'].label,
        valueProperty: 'UserDisplayPhoneNumber',
        valueGetter: (chatUser) => chatUser?.UserDisplayPhoneNumber || '',
      },
      {
        varName: currentLocale.implementedVariables['$contact.firstName'].name,
        label: currentLocale.implementedVariables['$contact.firstName'].label,
        valueProperty: 'FirstName',
        valueGetter: (chatUser) => chatUser?.FirstName || '',
      },
      {
        varName: '$contact.LastName',
        label: 'Last Name',
        valueProperty: 'LastName',
        valueGetter: (chatUser) => chatUser?.LastName || '',
      },
      {
        varName: '$contact.Email',
        label: 'Email',
        valueProperty: 'Email',
        valueGetter: (chatUser) => chatUser?.Email || '',
      },
      //* קישורי CRM קנוניים — עמודות הסנכרון החדשות ב-ChatUsers (ייתכן ריקות עד שאיש הקשר מקושר)
      {
        varName:
          currentLocale.implementedVariables['$contact.priorityCustomerId']?.name ||
          '$contact.priorityCustomerId',
        label:
          currentLocale.implementedVariables['$contact.priorityCustomerId']?.label ||
          'מזהה לקוח בפריוריטי',
        valueProperty: 'PriorityCustomerId',
        valueGetter: (chatUser) => chatUser?.PriorityCustomerId || '',
      },
      {
        varName:
          currentLocale.implementedVariables['$contact.priorityContactId']?.name ||
          '$contact.priorityContactId',
        label:
          currentLocale.implementedVariables['$contact.priorityContactId']?.label ||
          'מזהה איש קשר בפריוריטי',
        valueProperty: 'PriorityContactId',
        valueGetter: (chatUser) => chatUser?.PriorityContactId || '',
      },
      {
        varName:
          currentLocale.implementedVariables['$contact.fireberryAccountId']?.name ||
          '$contact.fireberryAccountId',
        label:
          currentLocale.implementedVariables['$contact.fireberryAccountId']?.label ||
          'מזהה לקוח בפיירברי',
        valueProperty: 'FireberryAccountId',
        valueGetter: (chatUser) => chatUser?.FireberryAccountId || '',
      },
      {
        varName:
          currentLocale.implementedVariables['$contact.fireberryContactId']?.name ||
          '$contact.fireberryContactId',
        label:
          currentLocale.implementedVariables['$contact.fireberryContactId']?.label ||
          'מזהה איש קשר בפיירברי',
        valueProperty: 'FireberryContactId',
        valueGetter: (chatUser) => chatUser?.FireberryContactId || '',
      },
    ];

    const systemVarsWithChildren: Array<SystemVariable> = [
      {
        varName: '$contact.LastIncomingMessage',
        label: 'Last Incoming Message',
        valueProperty: 'LastIncomingMessage',
        valueGetter: () => 'Last Incoming Message',
        children: (chatUser) =>
          chatUser?.LastIncomingMessage &&
          Object.keys(chatUser.LastIncomingMessage).map(
            (msgKey) =>
              ({
                varName: msgKey,
                label: msgKey,
                valueProperty: msgKey,
                valueGetter: (chatUser) => chatUser?.LastIncomingMessage[msgKey],
              }) as SystemVariable,
          ),
      },
      {
        varName: '$contact.LastOutgoingMessage',
        label: 'Last Outgoing Message',
        valueProperty: 'LastOutgoingMsgObject',
        valueGetter: () => 'Last Outgoing Message',
        children: (chatUser) =>
          chatUser?.LastOutgoingMsgObject &&
          Object.keys(chatUser.LastOutgoingMsgObject).map(
            (msgKey) =>
              ({
                varName: msgKey,
                label: msgKey,
                valueProperty: msgKey,
                valueGetter: (chatUser) => chatUser?.LastOutgoingMsgObject[msgKey],
              }) as SystemVariable,
          ),
      },
    ];

    const conversationVars: Array<ConversationVariable> = [
      {
        varName: currentLocale.implementedVariables['$conversation.opened_timestamp'].name,
        label: currentLocale.implementedVariables['$conversation.opened_timestamp'].label,
      },
      {
        varName: currentLocale.implementedVariables['$conversation.opened_by_source'].name,
        label: currentLocale.implementedVariables['$conversation.opened_by_source'].label,
      },
      {
        varName: currentLocale.implementedVariables['$conversation.opened_by_channel'].name,
        label: currentLocale.implementedVariables['$conversation.opened_by_channel'].label,
      },
      {
        varName: currentLocale.implementedVariables['$conversation.contact_type'].name,
        label: currentLocale.implementedVariables['$conversation.contact_type'].label,
      },
      {
        varName: currentLocale.implementedVariables['$conversation.first_incoming_message'].name,
        label: currentLocale.implementedVariables['$conversation.first_incoming_message'].label,
      },
      {
        varName: currentLocale.implementedVariables['$conversation.first_incoming_message_channel_id'].name,
        label: currentLocale.implementedVariables['$conversation.first_incoming_message_channel_id'].label,
      },
    ];

    return { systemVars, systemVarsWithChildren, conversationVars };
  }, [currentLocale]);

  return { systemVars, systemVarsWithChildren, conversationVars };
};
export default useSystemVars;
